import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import CollapsibleSection from '../components/CollapsibleSection';
import { 
  getOnboardingCandidate, 
  updateCandidateStatus, 
  deleteCandidate, 
  markCandidateGreen,
  getCandidateReferences,
  markReferenceContacted,
  unmarkReferenceContacted,
  Reference
} from '../api/onboardingService';
import { updatePrepareEnrichStatus } from '../api/prepareEnrichService';
import { getImageUrl } from '../utils/imageUrl';
import { calculateOnboardingProgress } from '../utils/progress';

const OnboardingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const candidateId = parseInt(id || '0');

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [showPrepareEnrichModal, setShowPrepareEnrichModal] = useState(false);
  const [prepareEnrichStatus, setPrepareEnrichStatus] = useState('');
  const [prepareEnrichNotes, setPrepareEnrichNotes] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isMarkingGreen, setIsMarkingGreen] = useState(false);
  const [showReferenceModal, setShowReferenceModal] = useState(false);
  const [selectedReference, setSelectedReference] = useState<Reference | null>(null);
  const [referenceNotes, setReferenceNotes] = useState('');

  // Fetch candidate data
  const { data: candidate, isLoading } = useQuery({
    queryKey: ['onboardingCandidate', candidateId],
    queryFn: () => getOnboardingCandidate(candidateId),
    enabled: !!candidateId,
    retry: false,
  });

  // Fetch references
  const { data: references = [] } = useQuery({
    queryKey: ['candidateReferences', candidateId],
    queryFn: () => getCandidateReferences(candidateId),
    enabled: !!candidateId,
    retry: false,
  });

  const updateStatusMutation = useMutation({
    mutationFn: (status: string) => updateCandidateStatus(candidateId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboardingCandidate', candidateId] });
      queryClient.invalidateQueries({ queryKey: ['onboardingCandidates'] });
      setShowStatusModal(false);
    },
  });

  const updatePrepareEnrichMutation = useMutation({
    mutationFn: ({ status, notes }: { status: string; notes?: string }) =>
      updatePrepareEnrichStatus(candidateId, { status, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboardingCandidate', candidateId] });
      queryClient.invalidateQueries({ queryKey: ['onboardingCandidates'] });
      setShowPrepareEnrichModal(false);
      setPrepareEnrichStatus('');
      setPrepareEnrichNotes('');
    },
  });

  const deleteCandidateMutation = useMutation({
    mutationFn: () => deleteCandidate(candidateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboardingCandidates'] });
      navigate('/onboarding');
    },
  });

  const onMarkGreen = async () => {
    try {
      setIsMarkingGreen(true);
      await markCandidateGreen(candidateId);
      queryClient.invalidateQueries({ queryKey: ['onboardingCandidate', candidateId] });
      queryClient.invalidateQueries({ queryKey: ['onboardingCandidates'] });
      navigate('/planters');
    } catch (e) {
      console.error('Failed to promote to planter', e);
    } finally {
      setIsMarkingGreen(false);
    }
  };

  // Reference mutations
  const markReferenceContactedMutation = useMutation({
    mutationFn: ({ referenceId, notes }: { referenceId: number; notes?: string }) =>
      markReferenceContacted(candidateId, referenceId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidateReferences', candidateId] });
      queryClient.invalidateQueries({ queryKey: ['onboardingCandidate', candidateId] });
      queryClient.invalidateQueries({ queryKey: ['onboardingCandidates'] });
      setShowReferenceModal(false);
      setSelectedReference(null);
      setReferenceNotes('');
    },
  });

  const unmarkReferenceContactedMutation = useMutation({
    mutationFn: (referenceId: number) => unmarkReferenceContacted(candidateId, referenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidateReferences', candidateId] });
      queryClient.invalidateQueries({ queryKey: ['onboardingCandidate', candidateId] });
      queryClient.invalidateQueries({ queryKey: ['onboardingCandidates'] });
    },
  });

  const handleMarkReferenceContacted = (reference: Reference) => {
    if (reference.contactedByHub) {
      // If already contacted, unmark it
      if (window.confirm(`Unmark "${reference.name}" as contacted?`)) {
        unmarkReferenceContactedMutation.mutate(reference.id);
      }
    } else {
      // Open modal to mark as contacted
      setSelectedReference(reference);
      setReferenceNotes(reference.contactedByHubNotes || '');
      setShowReferenceModal(true);
    }
  };

  const submitReferenceContacted = () => {
    if (selectedReference) {
      markReferenceContactedMutation.mutate({
        referenceId: selectedReference.id,
        notes: referenceNotes,
      });
    }
  };

  const getStatusBadgeColor = (status: string) => {
    const colors: Record<string, string> = {
      exploring: 'bg-blue-100 text-blue-800',
      preassessment: 'bg-yellow-100 text-yellow-800',
      postponed: 'bg-orange-100 text-orange-800',
      archived: 'bg-gray-100 text-gray-600',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      exploring: 'Exploring',
      preassessment: 'Pre-Assessment',
      postponed: 'Postponed',
      archived: 'Archived',
    };
    return labels[status] || status;
  };

  /* ── helpers for Ministry Transitions rendering ── */
  const conclusionLabels: Record<string, string> = {
    called_new_position: 'Called to a new ministry position',
    fulfilled_purpose: 'Felt purpose was fulfilled in that role',
    mutual_decision: 'Mutual decision to transition',
    church_closed: 'Church closed or underwent major restructuring',
    personal_family: 'Personal or family reasons',
    conflict: 'Conflict or disagreement',
    asked_to_leave: 'Asked to leave',
    other: 'Other',
  };

  const wcpData = candidate?.whyChurchPlantingData as Record<string, any> | null | undefined;
  const mtData = candidate?.ministryTransitionsData as Record<string, any> | null | undefined;
  const mtPositions: any[] = mtData?.positions || [];

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  if (!candidate) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Candidate Not Found</h2>
          <p className="text-gray-600 mb-4">
            The backend endpoint for viewing portal candidates needs to be implemented.
          </p>
          <button
            onClick={() => navigate('/onboarding')}
            className="text-primary-600 hover:text-primary-700"
          >
            Back to Onboarding
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        {/* Sticky Back Button */}
        <div className="sticky top-16 z-40 bg-white shadow-sm mb-6 -mx-4 sm:mx-0 px-4 py-3 sm:rounded-lg">
          <button
            onClick={() => navigate('/onboarding')}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center"
          >
            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Pre-Assessment
          </button>
        </div>
        
        {/* Header */}
        <div className="mb-6">
          
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <Avatar 
                name={candidate.name}
                imageUrl={getImageUrl(candidate.headshotImagePath, { width: 128, quality: 70, format: 'webp' })}
                size="2xl"
              />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{candidate.name}</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Submitted: {new Date(candidate.createdAt).toLocaleDateString()}
                </p>
                {candidate.email && (
                  <p className="text-sm text-gray-500 mt-1">{candidate.email}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {candidate.portalStatus && (
                <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeColor(candidate.portalStatus)}`}>
                  {getStatusLabel(candidate.portalStatus)}
                </span>
              )}
              <button
                onClick={() => setShowStatusModal(true)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Update Status
              </button>
              <button
                onClick={onMarkGreen}
                disabled={isMarkingGreen}
                className="inline-flex items-center px-3 py-2 border border-green-300 shadow-sm text-sm leading-4 font-medium rounded-md text-green-700 bg-white hover:bg-green-50 disabled:opacity-50"
                title="Create a church planter profile from this portal candidate"
              >
                {isMarkingGreen ? 'Promoting…' : 'Promote to planter'}
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">First Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{candidate.firstName || 'Not provided'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{candidate.lastName || 'Not provided'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">{candidate.email || 'Not provided'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1 text-sm text-gray-900">{candidate.phone || 'Not provided'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Date of Birth</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {candidate.dateOfBirth ? new Date(candidate.dateOfBirth).toLocaleDateString() : 'Not provided'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Marital Status</dt>
                  <dd className="mt-1 text-sm text-gray-900">{candidate.maritalStatus || 'Not provided'}</dd>
                </div>
              </dl>
            </div>

            {/* Family Information */}
            {candidate.maritalStatus === 'married' && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Family Information</h2>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Wife's First Name</dt>
                    <dd className="mt-1 text-sm text-gray-900">{candidate.wifeFirstName || 'Not provided'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Wife's Last Name</dt>
                    <dd className="mt-1 text-sm text-gray-900">{candidate.wifeLastName || 'Not provided'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Wife's Email</dt>
                    <dd className="mt-1 text-sm text-gray-900">{candidate.wifeEmail || 'Not provided'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Wife's Phone</dt>
                    <dd className="mt-1 text-sm text-gray-900">{candidate.wifePhone || 'Not provided'}</dd>
                  </div>
                  {candidate.childrenNamesAges && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Children Names & Ages</dt>
                      <dd className="mt-1 text-sm text-gray-900">{candidate.childrenNamesAges}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* Ministry Information */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Ministry Information</h2>
              <dl className="space-y-4">
                {candidate.targetLocationMississippi && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Target Location in Mississippi</dt>
                    <dd className="mt-1 text-sm text-gray-900">{candidate.targetLocationMississippi}</dd>
                  </div>
                )}
                {candidate.willBeSouthernBaptist && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Will Be Southern Baptist?</dt>
                    <dd className="mt-1 text-sm text-gray-900">{candidate.willBeSouthernBaptist}</dd>
                  </div>
                )}
                {candidate.ministryLeadershipPositions && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Ministry Leadership Positions</dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{candidate.ministryLeadershipPositions}</dd>
                  </div>
                )}
                {candidate.ministryInfluences && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Ministry Influences</dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{candidate.ministryInfluences}</dd>
                  </div>
                )}
                {candidate.theologicalTraining && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Theological Training</dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalTraining}</dd>
                  </div>
                )}
                {candidate.sermonReferenceUrl && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Sermon Reference</dt>
                    <dd className="mt-1 text-sm">
                      <a 
                        href={candidate.sermonReferenceUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700"
                      >
                        {candidate.sermonReferenceUrl}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Why Church Planting */}
            <CollapsibleSection
              title="Why Church Planting"
              badge={
                candidate.whyChurchPlantingCompleted ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">Completed</span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">Not Completed</span>
                )
              }
            >
              {wcpData && (wcpData.whyPlanting || wcpData.visionForChurch || wcpData.howDecisionMade || wcpData.additionalThoughts) ? (
                <dl className="space-y-4">
                  {wcpData.whyPlanting && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Why do you want to plant a church?</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{wcpData.whyPlanting}</dd>
                    </div>
                  )}
                  {wcpData.visionForChurch && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">What is your vision for the church?</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{wcpData.visionForChurch}</dd>
                    </div>
                  )}
                  {wcpData.howDecisionMade && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">How did you come to this decision?</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{wcpData.howDecisionMade}</dd>
                    </div>
                  )}
                  {wcpData.additionalThoughts && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Additional thoughts</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{wcpData.additionalThoughts}</dd>
                    </div>
                  )}
                </dl>
              ) : (
                <p className="text-sm text-gray-600">Not submitted yet.</p>
              )}
            </CollapsibleSection>

            {/* Sending Church */}
            <CollapsibleSection title="Sending Church">
              {candidate.sendingChurch ? (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {candidate.sendingChurch.churchName && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Church Name</dt>
                      <dd className="mt-1 text-sm text-gray-900">{candidate.sendingChurch.churchName}</dd>
                    </div>
                  )}
                  {candidate.sendingChurch.primaryContactName && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Primary Contact</dt>
                      <dd className="mt-1 text-sm text-gray-900">{candidate.sendingChurch.primaryContactName}</dd>
                    </div>
                  )}
                  {candidate.sendingChurch.primaryContactPhone && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Contact Phone</dt>
                      <dd className="mt-1 text-sm text-gray-900">{candidate.sendingChurch.primaryContactPhone}</dd>
                    </div>
                  )}
                  {candidate.sendingChurch.noSendingChurch && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">No Sending Church</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        Yes {candidate.sendingChurch.noSendingChurchExplanation && `— ${candidate.sendingChurch.noSendingChurchExplanation}`}
                      </dd>
                    </div>
                  )}
                </dl>
              ) : (
                <p className="text-sm text-gray-600">No sending church data submitted.</p>
              )}
            </CollapsibleSection>

            {/* Spiritual/Emotional Health Survey */}
            {candidate.spiritualHealthSurveys && candidate.spiritualHealthSurveys.length > 0 && (
              <CollapsibleSection
                title="Spiritual/Emotional Health Assessment"
                badge={
                  candidate.spiritualHealthCompleted ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">Completed</span>
                  ) : undefined
                }
              >
                <div className="space-y-8">
                  {candidate.spiritualHealthSurveys.map((survey) => (
                    <div key={survey.id} className="border-l-4 border-primary-500 pl-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-md font-semibold text-gray-800 capitalize">
                          {survey.surveyFor} Survey
                        </h3>
                        {survey.status === 'submitted' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                            <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Submitted
                          </span>
                        )}
                      </div>
                      
                      <dl className="space-y-6">
                        {/* Section 1: Spiritual Health Awareness */}
                        {(survey.currentWalkWithLord || survey.intimacyWithChristHindrances || survey.spiritualDrynessResponse || survey.soulGuarding || survey.restAndSabbathRhythms || survey.recentConvictionResponse) && (
                          <div className="border-b border-gray-200 pb-4">
                            <dt className="text-sm font-semibold text-gray-700 mb-3">Section 1: Spiritual Health Awareness</dt>
                            <div className="space-y-3">
                              {survey.currentWalkWithLord && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Current walk with the Lord</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.currentWalkWithLord}</dd>
                                </div>
                              )}
                              {survey.intimacyWithChristHindrances && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">What pulls you away from intimacy with Christ</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.intimacyWithChristHindrances}</dd>
                                </div>
                              )}
                              {survey.spiritualDrynessResponse && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Handling spiritual dryness or discouragement</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.spiritualDrynessResponse}</dd>
                                </div>
                              )}
                              {survey.soulGuarding && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Guarding your soul</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.soulGuarding}</dd>
                                </div>
                              )}
                              {survey.restAndSabbathRhythms && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Rest and Sabbath rhythms</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.restAndSabbathRhythms}</dd>
                                </div>
                              )}
                              {survey.recentConvictionResponse && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Recent conviction response</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.recentConvictionResponse}</dd>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Section 2: Emotional Health Awareness */}
                        {(survey.emotionalStabilityDescription || survey.pressureCriticismResponse || survey.stressDisappointmentHandling || survey.healthyBoundaries || survey.currentEmotionalState || survey.burnoutPrevention) && (
                          <div className="border-b border-gray-200 pb-4">
                            <dt className="text-sm font-semibold text-gray-700 mb-3">Section 2: Emotional Health Awareness</dt>
                            <div className="space-y-3">
                              {survey.emotionalStabilityDescription && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Emotional stability and temperament</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.emotionalStabilityDescription}</dd>
                                </div>
                              )}
                              {survey.pressureCriticismResponse && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Response to pressure or criticism</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.pressureCriticismResponse}</dd>
                                </div>
                              )}
                              {survey.stressDisappointmentHandling && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Handling stress or disappointment</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.stressDisappointmentHandling}</dd>
                                </div>
                              )}
                              {survey.healthyBoundaries && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Healthy boundaries</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.healthyBoundaries}</dd>
                                </div>
                              )}
                              {survey.currentEmotionalState && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Current emotional state</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.currentEmotionalState}</dd>
                                </div>
                              )}
                              {survey.burnoutPrevention && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Burnout prevention</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.burnoutPrevention}</dd>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Section 3: Calling and Resilience */}
                        {(survey.godsCallDescription || survey.callConfirmationByOthers || survey.difficultMoments || survey.discerningGodsLeading || survey.faithAndWisdomBalance || survey.churchPlantingFears) && (
                          <div className="border-b border-gray-200 pb-4">
                            <dt className="text-sm font-semibold text-gray-700 mb-3">Section 3: Calling and Resilience</dt>
                            <div className="space-y-3">
                              {survey.godsCallDescription && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">God's call to church planting</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.godsCallDescription}</dd>
                                </div>
                              )}
                              {survey.callConfirmationByOthers && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Call confirmed by others</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.callConfirmationByOthers}</dd>
                                </div>
                              )}
                              {survey.difficultMoments && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Most difficult moments</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.difficultMoments}</dd>
                                </div>
                              )}
                              {survey.discerningGodsLeading && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Discerning God's leading</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.discerningGodsLeading}</dd>
                                </div>
                              )}
                              {survey.faithAndWisdomBalance && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Balance of faith and wisdom</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.faithAndWisdomBalance}</dd>
                                </div>
                              )}
                              {survey.churchPlantingFears && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Church planting fears</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.churchPlantingFears}</dd>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Section 4: Relational Health Awareness */}
                        {(survey.feedbackCorrectionResponse || survey.conflictResolutionApproach || survey.shepherdingDifficultPeople || survey.fosteringUnity || survey.relationalIntegrity || survey.accountabilityRelationships) && (
                          <div className="border-b border-gray-200 pb-4">
                            <dt className="text-sm font-semibold text-gray-700 mb-3">Section 4: Relational Health Awareness</dt>
                            <div className="space-y-3">
                              {survey.feedbackCorrectionResponse && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Response to feedback or correction</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.feedbackCorrectionResponse}</dd>
                                </div>
                              )}
                              {survey.conflictResolutionApproach && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Conflict resolution approach</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.conflictResolutionApproach}</dd>
                                </div>
                              )}
                              {survey.shepherdingDifficultPeople && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Shepherding difficult people</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.shepherdingDifficultPeople}</dd>
                                </div>
                              )}
                              {survey.fosteringUnity && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Fostering unity</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.fosteringUnity}</dd>
                                </div>
                              )}
                              {survey.relationalIntegrity && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Relational integrity</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.relationalIntegrity}</dd>
                                </div>
                              )}
                              {survey.accountabilityRelationships && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Accountability relationships</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.accountabilityRelationships}</dd>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Section 5: Spiritual Warfare Health Awareness */}
                        {(survey.spiritualAttackRecognition || survey.prayerInDecisions || survey.guardingAgainstCynicism || survey.godlyProvisionExample || survey.prayingTogether || survey.sustainingScriptures) && (
                          <div className="border-b border-gray-200 pb-4">
                            <dt className="text-sm font-semibold text-gray-700 mb-3">Section 5: Spiritual Warfare Health Awareness</dt>
                            <div className="space-y-3">
                              {survey.spiritualAttackRecognition && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Spiritual attack recognition</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.spiritualAttackRecognition}</dd>
                                </div>
                              )}
                              {survey.prayerInDecisions && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Prayer in decisions</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.prayerInDecisions}</dd>
                                </div>
                              )}
                              {survey.guardingAgainstCynicism && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Guarding against cynicism</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.guardingAgainstCynicism}</dd>
                                </div>
                              )}
                              {survey.godlyProvisionExample && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Godly provision example</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.godlyProvisionExample}</dd>
                                </div>
                              )}
                              {survey.prayingTogether && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Praying together</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.prayingTogether}</dd>
                                </div>
                              )}
                              {survey.sustainingScriptures && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Sustaining Scriptures</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.sustainingScriptures}</dd>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Section 6: Accountability Health Awareness */}
                        {(survey.pastorMentorAccountability || survey.discipleshipFrequency || survey.teachability || survey.isolationPrevention || survey.spouseSupportNeeds || survey.handlingUnappreciation) && (
                          <div className="pb-4">
                            <dt className="text-sm font-semibold text-gray-700 mb-3">Section 6: Accountability Health Awareness</dt>
                            <div className="space-y-3">
                              {survey.pastorMentorAccountability && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Pastor, mentor, accountability partner</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.pastorMentorAccountability}</dd>
                                </div>
                              )}
                              {survey.discipleshipFrequency && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Discipleship frequency</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.discipleshipFrequency}</dd>
                                </div>
                              )}
                              {survey.teachability && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Teachability</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.teachability}</dd>
                                </div>
                              )}
                              {survey.isolationPrevention && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Isolation prevention</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.isolationPrevention}</dd>
                                </div>
                              )}
                              {survey.spouseSupportNeeds && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Spouse support needs</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.spouseSupportNeeds}</dd>
                                </div>
                              )}
                              {survey.handlingUnappreciation && (
                                <div>
                                  <dt className="text-xs font-medium text-gray-600 mb-1">Handling unappreciation</dt>
                                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{survey.handlingUnappreciation}</dd>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </dl>
                      
                      {survey.submittedAt && (
                        <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
                          Submitted: {new Date(survey.submittedAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Theological Survey */}
            {candidate.theologicalSurvey && (
              <CollapsibleSection
                title="Theological Survey"
                badge={
                  candidate.theologyCompleted ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">Completed</span>
                  ) : undefined
                }
              >
                <dl className="space-y-6">
                  {/* Scripture */}
                  {candidate.theologicalSurvey.scriptureAffirmation && (
                    <div className="border-b border-gray-200 pb-4">
                      <dt className="text-sm font-medium text-gray-700 mb-2">Scripture</dt>
                      <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.scriptureAffirmation}</dd>
                    </div>
                  )}
                  
                  {/* God & Trinity */}
                  {candidate.theologicalSurvey.trinityUnderstanding && (
                    <div className="border-b border-gray-200 pb-4">
                      <dt className="text-sm font-medium text-gray-700 mb-2">God & the Trinity</dt>
                      <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.trinityUnderstanding}</dd>
                    </div>
                  )}
                  
                  {/* Humanity & Sin */}
                  {candidate.theologicalSurvey.originalSinUnderstanding && (
                    <div className="border-b border-gray-200 pb-4">
                      <dt className="text-sm font-medium text-gray-700 mb-2">Humanity & Sin</dt>
                      <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.originalSinUnderstanding}</dd>
                    </div>
                  )}
                  
                  {/* Christology */}
                  {candidate.theologicalSurvey.christologyUnderstanding && (
                    <div className="border-b border-gray-200 pb-4">
                      <dt className="text-sm font-medium text-gray-700 mb-2">Christology</dt>
                      <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.christologyUnderstanding}</dd>
                    </div>
                  )}
                  
                  {/* Salvation */}
                  {(candidate.theologicalSurvey.salvationDoctrines || candidate.theologicalSurvey.gospelPresentation) && (
                    <div className="border-b border-gray-200 pb-4">
                      <dt className="text-sm font-medium text-gray-700 mb-2">Salvation</dt>
                      {candidate.theologicalSurvey.salvationDoctrines && (
                        <dd className="text-sm text-gray-900 whitespace-pre-wrap mb-3">{candidate.theologicalSurvey.salvationDoctrines}</dd>
                      )}
                      {candidate.theologicalSurvey.gospelPresentation && (
                        <div>
                          <dt className="text-xs font-medium text-gray-600 mb-1">Gospel Presentation</dt>
                          <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.gospelPresentation}</dd>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Ecclesiology */}
                  {(candidate.theologicalSurvey.churchNatureMission || candidate.theologicalSurvey.churchOrdinances || candidate.theologicalSurvey.churchMembership || candidate.theologicalSurvey.churchOffices) && (
                    <div className="border-b border-gray-200 pb-4">
                      <dt className="text-sm font-medium text-gray-700 mb-2">Ecclesiology</dt>
                      {candidate.theologicalSurvey.churchNatureMission && (
                        <dd className="text-sm text-gray-900 whitespace-pre-wrap mb-2">{candidate.theologicalSurvey.churchNatureMission}</dd>
                      )}
                      {candidate.theologicalSurvey.churchOrdinances && (
                        <div className="mb-2">
                          <dt className="text-xs font-medium text-gray-600 mb-1">Ordinances</dt>
                          <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.churchOrdinances}</dd>
                        </div>
                      )}
                      {candidate.theologicalSurvey.churchMembership && (
                        <div className="mb-2">
                          <dt className="text-xs font-medium text-gray-600 mb-1">Membership</dt>
                          <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.churchMembership}</dd>
                        </div>
                      )}
                      {candidate.theologicalSurvey.churchOffices && (
                        <div>
                          <dt className="text-xs font-medium text-gray-600 mb-1">Offices</dt>
                          <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.churchOffices}</dd>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Eschatology */}
                  {(candidate.theologicalSurvey.eschatologicalConvictions || candidate.theologicalSurvey.eschatologyMinistryImpact) && (
                    <div className="border-b border-gray-200 pb-4">
                      <dt className="text-sm font-medium text-gray-700 mb-2">Eschatology</dt>
                      {candidate.theologicalSurvey.eschatologicalConvictions && (
                        <dd className="text-sm text-gray-900 whitespace-pre-wrap mb-3">{candidate.theologicalSurvey.eschatologicalConvictions}</dd>
                      )}
                      {candidate.theologicalSurvey.eschatologyMinistryImpact && (
                        <div>
                          <dt className="text-xs font-medium text-gray-600 mb-1">Ministry Impact</dt>
                          <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.eschatologyMinistryImpact}</dd>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Baptist Distinctives */}
                  {(candidate.theologicalSurvey.believersBaptism || candidate.theologicalSurvey.churchAutonomy) && (
                    <div className="border-b border-gray-200 pb-4">
                      <dt className="text-sm font-medium text-gray-700 mb-2">Baptist Distinctives</dt>
                      {candidate.theologicalSurvey.believersBaptism && (
                        <dd className="text-sm text-gray-900 whitespace-pre-wrap mb-3">{candidate.theologicalSurvey.believersBaptism}</dd>
                      )}
                      {candidate.theologicalSurvey.churchAutonomy && (
                        <div>
                          <dt className="text-xs font-medium text-gray-600 mb-1">Church Autonomy</dt>
                          <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.churchAutonomy}</dd>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Ministry Philosophy & Practice */}
                  {(candidate.theologicalSurvey.preachingPhilosophy || candidate.theologicalSurvey.discipleMaking || candidate.theologicalSurvey.worshipPhilosophy || candidate.theologicalSurvey.evangelismSocialEngagement || candidate.theologicalSurvey.theologicalDisagreement) && (
                    <div className="border-b border-gray-200 pb-4">
                      <dt className="text-sm font-medium text-gray-700 mb-2">Ministry Philosophy & Practice</dt>
                      <div className="space-y-3">
                        {candidate.theologicalSurvey.preachingPhilosophy && (
                          <div>
                            <dt className="text-xs font-medium text-gray-600 mb-1">Preaching Philosophy</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.preachingPhilosophy}</dd>
                          </div>
                        )}
                        {candidate.theologicalSurvey.discipleMaking && (
                          <div>
                            <dt className="text-xs font-medium text-gray-600 mb-1">Disciple Making</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.discipleMaking}</dd>
                          </div>
                        )}
                        {candidate.theologicalSurvey.worshipPhilosophy && (
                          <div>
                            <dt className="text-xs font-medium text-gray-600 mb-1">Worship Philosophy</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.worshipPhilosophy}</dd>
                          </div>
                        )}
                        {candidate.theologicalSurvey.evangelismSocialEngagement && (
                          <div>
                            <dt className="text-xs font-medium text-gray-600 mb-1">Evangelism & Social Engagement</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.evangelismSocialEngagement}</dd>
                          </div>
                        )}
                        {candidate.theologicalSurvey.theologicalDisagreement && (
                          <div>
                            <dt className="text-xs font-medium text-gray-600 mb-1">Handling Theological Disagreement</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.theologicalDisagreement}</dd>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Missional & Cultural Engagement */}
                  {(candidate.theologicalSurvey.gospelPluralisticCulture || candidate.theologicalSurvey.contextualizationView || candidate.theologicalSurvey.plantingChallenges) && (
                    <div className="border-b border-gray-200 pb-4">
                      <dt className="text-sm font-medium text-gray-700 mb-2">Missional & Cultural Engagement</dt>
                      <div className="space-y-3">
                        {candidate.theologicalSurvey.gospelPluralisticCulture && (
                          <div>
                            <dt className="text-xs font-medium text-gray-600 mb-1">Gospel in Pluralistic Culture</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.gospelPluralisticCulture}</dd>
                          </div>
                        )}
                        {candidate.theologicalSurvey.contextualizationView && (
                          <div>
                            <dt className="text-xs font-medium text-gray-600 mb-1">Contextualization</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.contextualizationView}</dd>
                          </div>
                        )}
                        {candidate.theologicalSurvey.plantingChallenges && (
                          <div>
                            <dt className="text-xs font-medium text-gray-600 mb-1">Church Planting Challenges</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.plantingChallenges}</dd>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Personal & Family Life */}
                  {(candidate.theologicalSurvey.personalHoliness || candidate.theologicalSurvey.marriageFamilyProtection || candidate.theologicalSurvey.conflictHandling) && (
                    <div className="pb-4">
                      <dt className="text-sm font-medium text-gray-700 mb-2">Personal & Family Life</dt>
                      <div className="space-y-3">
                        {candidate.theologicalSurvey.personalHoliness && (
                          <div>
                            <dt className="text-xs font-medium text-gray-600 mb-1">Personal Holiness</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.personalHoliness}</dd>
                          </div>
                        )}
                        {candidate.theologicalSurvey.marriageFamilyProtection && (
                          <div>
                            <dt className="text-xs font-medium text-gray-600 mb-1">Marriage & Family Protection</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.marriageFamilyProtection}</dd>
                          </div>
                        )}
                        {candidate.theologicalSurvey.conflictHandling && (
                          <div>
                            <dt className="text-xs font-medium text-gray-600 mb-1">Conflict Handling</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.theologicalSurvey.conflictHandling}</dd>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </dl>
                {candidate.theologicalSurvey.submittedAt && (
                  <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
                    Submitted: {new Date(candidate.theologicalSurvey.submittedAt).toLocaleDateString()}
                  </div>
                )}
              </CollapsibleSection>
            )}

            {/* Spouse Interview */}
            {candidate.maritalStatus === 'married' && (
              <CollapsibleSection
                title="Spouse Interview"
                badge={
                  candidate.spouseInterviewSubmitted ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">Submitted</span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">Pending</span>
                  )
                }
              >
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="mt-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        candidate.spouseInterviewStatus === 'complete' ? 'bg-green-100 text-green-800' :
                        candidate.spouseInterviewStatus === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                        candidate.spouseInterviewStatus === 'submitted' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {candidate.spouseInterviewStatus || 'pending'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Submitted</dt>
                    <dd className="mt-1 text-sm text-gray-900">{candidate.spouseInterviewSubmitted ? 'Yes' : 'No'}</dd>
                  </div>
                  {candidate.spouseInterviewSubmittedAt && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Submitted At</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {new Date(candidate.spouseInterviewSubmittedAt).toLocaleDateString()}
                      </dd>
                    </div>
                  )}
                  {candidate.spouseInterviewNotes && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Notes</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{candidate.spouseInterviewNotes}</dd>
                    </div>
                  )}
                </dl>

                {/* Spouse Interview Questionnaire Responses */}
                {candidate.spouseInterviewData?.questionnaire && (
                  <div className="mt-6 border-t border-gray-200 pt-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-4">Spouse Pre-Assessment Questionnaire</h4>
                    <dl className="space-y-6">
                      {/* Section 1: Calling & Vision */}
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Calling & Vision</h5>
                        {candidate.spouseInterviewData.questionnaire.futureRoleAsWife && (
                          <div className="border-b border-gray-200 pb-4 mb-4">
                            <dt className="text-sm font-medium text-gray-700 mb-2">How would you define your future role as the wife of a church planter?</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.spouseInterviewData.questionnaire.futureRoleAsWife}</dd>
                          </div>
                        )}
                        {candidate.spouseInterviewData.questionnaire.excitementAboutPlanting && (
                          <div className="border-b border-gray-200 pb-4 mb-4">
                            <dt className="text-sm font-medium text-gray-700 mb-2">What excites you most about the prospect of church planting?</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.spouseInterviewData.questionnaire.excitementAboutPlanting}</dd>
                          </div>
                        )}
                        {candidate.spouseInterviewData.questionnaire.certaintyOfHusbandsCalling && (
                          <div className="border-b border-gray-200 pb-4 mb-4">
                            <dt className="text-sm font-medium text-gray-700 mb-2">Level of certainty that husband has been called to plant and lead a church</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.spouseInterviewData.questionnaire.certaintyOfHusbandsCalling}</dd>
                          </div>
                        )}
                        {candidate.spouseInterviewData.questionnaire.confidenceInSpouseAbility && (
                          <div className="border-b border-gray-200 pb-4 mb-4">
                            <dt className="text-sm font-medium text-gray-700 mb-2">Level of confidence in spouse's ability to plant a church</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.spouseInterviewData.questionnaire.confidenceInSpouseAbility}</dd>
                          </div>
                        )}
                      </div>

                      {/* Section 2: Ministry & Gifts */}
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Ministry & Gifts</h5>
                        {candidate.spouseInterviewData.questionnaire.callValidators && (
                          <div className="border-b border-gray-200 pb-4 mb-4">
                            <dt className="text-sm font-medium text-gray-700 mb-2">Names of those who have validated the call to plant</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.spouseInterviewData.questionnaire.callValidators}</dd>
                          </div>
                        )}
                        {candidate.spouseInterviewData.questionnaire.currentMinistryInvolvement && (
                          <div className="border-b border-gray-200 pb-4 mb-4">
                            <dt className="text-sm font-medium text-gray-700 mb-2">Current ministry involvement and level of fulfillment</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.spouseInterviewData.questionnaire.currentMinistryInvolvement}</dd>
                          </div>
                        )}
                        {candidate.spouseInterviewData.questionnaire.spiritualGifts && (
                          <div className="border-b border-gray-200 pb-4 mb-4">
                            <dt className="text-sm font-medium text-gray-700 mb-2">Spiritual gifts and how they're expressed in ministry</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.spouseInterviewData.questionnaire.spiritualGifts}</dd>
                          </div>
                        )}
                        {candidate.spouseInterviewData.questionnaire.serviceToGodDream && (
                          <div className="border-b border-gray-200 pb-4 mb-4">
                            <dt className="text-sm font-medium text-gray-700 mb-2">If you could do anything in your service to God, what would it be?</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.spouseInterviewData.questionnaire.serviceToGodDream}</dd>
                          </div>
                        )}
                      </div>

                      {/* Section 3: Personal & Family */}
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Personal & Family</h5>
                        {candidate.spouseInterviewData.questionnaire.salvationTestimony && (
                          <div className="border-b border-gray-200 pb-4 mb-4">
                            <dt className="text-sm font-medium text-gray-700 mb-2">Salvation testimony</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.spouseInterviewData.questionnaire.salvationTestimony}</dd>
                          </div>
                        )}
                        {candidate.spouseInterviewData.questionnaire.workFamilyBalance && (
                          <div className="border-b border-gray-200 pb-4 mb-4">
                            <dt className="text-sm font-medium text-gray-700 mb-2">Work/family balance</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.spouseInterviewData.questionnaire.workFamilyBalance}</dd>
                          </div>
                        )}
                        {candidate.spouseInterviewData.questionnaire.parentalRelationships && (
                          <div className="border-b border-gray-200 pb-4 mb-4">
                            <dt className="text-sm font-medium text-gray-700 mb-2">Relationship with parents</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.spouseInterviewData.questionnaire.parentalRelationships}</dd>
                          </div>
                        )}
                        {candidate.spouseInterviewData.questionnaire.addictionStruggles && (
                          <div className="border-b border-gray-200 pb-4 mb-4">
                            <dt className="text-sm font-medium text-gray-700 mb-2">Addiction struggles</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.spouseInterviewData.questionnaire.addictionStruggles}</dd>
                          </div>
                        )}
                        {candidate.spouseInterviewData.questionnaire.guiltShameStruggles && (
                          <div className="pb-4">
                            <dt className="text-sm font-medium text-gray-700 mb-2">Guilt, shame, or low self-esteem struggles</dt>
                            <dd className="text-sm text-gray-900 whitespace-pre-wrap">{candidate.spouseInterviewData.questionnaire.guiltShameStruggles}</dd>
                          </div>
                        )}
                      </div>
                    </dl>
                  </div>
                )}
              </CollapsibleSection>
            )}

            {/* Ministry Transitions */}
            <CollapsibleSection
              title="Ministry Transitions"
              badge={
                candidate.ministryTransitionsCompleted ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">Completed</span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">Not Completed</span>
                )
              }
            >
              {mtData ? (
                <div className="space-y-6">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Has served on church staff?</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {mtData.hasServedOnStaff === true ? 'Yes' : mtData.hasServedOnStaff === false ? 'No' : '—'}
                    </dd>
                  </div>

                  {mtData.hasServedOnStaff === false && mtData.ministryBackground && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Ministry background</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{mtData.ministryBackground}</dd>
                    </div>
                  )}

                  {mtPositions.map((pos: any, idx: number) => (
                    <div key={idx} className="rounded-lg border border-gray-200 p-4 space-y-3">
                      <h4 className="text-sm font-semibold text-gray-800">
                        {idx === 0 ? 'Most Recent Staff Position' : 'Previous Staff Position'}
                        {pos.isCurrentPosition && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Current
                          </span>
                        )}
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {pos.churchName && (
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Church / Organization</dt>
                            <dd className="text-sm text-gray-900 mt-0.5">{pos.churchName}</dd>
                          </div>
                        )}
                        {pos.role && (
                          <div>
                            <dt className="text-xs font-medium text-gray-500">Role / Title</dt>
                            <dd className="text-sm text-gray-900 mt-0.5">{pos.role}</dd>
                          </div>
                        )}
                        {pos.datesServed && (
                          <div>
                            <dt className="text-xs font-medium text-gray-500">
                              {pos.isCurrentPosition ? 'Start date' : 'Dates served'}
                            </dt>
                            <dd className="text-sm text-gray-900 mt-0.5">{pos.datesServed}</dd>
                          </div>
                        )}
                      </div>
                      {!pos.isCurrentPosition && (
                        <div className="space-y-3 pt-2 border-t border-gray-100">
                          {pos.howConcluded && (
                            <div>
                              <dt className="text-xs font-medium text-gray-500">How did this position conclude?</dt>
                              <dd className="text-sm text-gray-900 mt-0.5">
                                {conclusionLabels[pos.howConcluded] || pos.howConcluded}
                              </dd>
                            </div>
                          )}
                          {pos.unresolvedConflict && (
                            <div>
                              <dt className="text-xs font-medium text-gray-500">Any unresolved conflict?</dt>
                              <dd className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap">{pos.unresolvedConflict}</dd>
                            </div>
                          )}
                          {pos.leadershipFavorability && (
                            <div>
                              <dt className="text-xs font-medium text-gray-500">Would leadership speak favorably?</dt>
                              <dd className="text-sm text-gray-900 mt-0.5 whitespace-pre-wrap">{pos.leadershipFavorability}</dd>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {mtData.additionalInfo && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Additional information</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{mtData.additionalInfo}</dd>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-600">Not submitted yet.</p>
              )}
            </CollapsibleSection>
          </div>

          {/* Sidebar Column */}
          <div className="space-y-6">
            {/* Progress */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Progress</h2>
              <div className="mb-4">
                {(() => {
                  const portalPct =
                    typeof candidate.portalProgressPercentage === 'number'
                      ? candidate.portalProgressPercentage
                      : calculateOnboardingProgress(candidate, references);
                  const teamPct = typeof candidate.teamProgressPercentage === 'number' ? candidate.teamProgressPercentage : 0;

                  return (
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                          <span className="font-semibold uppercase tracking-wide">Portal</span>
                          <span>
                            Step {candidate.onboardingStep} of 10 • {portalPct}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-primary-600 h-2 rounded-full transition-all" style={{ width: `${portalPct}%` }} />
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                          <span className="font-semibold uppercase tracking-wide">Team</span>
                          <span>{teamPct}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-emerald-600 h-2 rounded-full transition-all" style={{ width: `${teamPct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              {candidate.onboardingCompleted && (
                <div className="flex items-center text-sm text-green-600">
                  <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Candidate Assessment Completed
                </div>
              )}
            </div>

            {/* All Categories Checklist */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Category Completion</h2>
              <div className="space-y-2">
                {(() => {
                  const greenCheck = (
                    <svg className="h-5 w-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  );
                  const yellowClock = (
                    <svg className="h-5 w-5 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                  );
                  const grayCircle = (
                    <svg className="h-5 w-5 text-gray-300 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
                    </svg>
                  );
                  const naIcon = (
                    <svg className="h-5 w-5 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                    </svg>
                  );

                  const items: Array<{ label: string; icon: React.ReactNode }> = [
                    { label: 'Create Profile', icon: candidate.profileCompleted || candidate.onboardingStep >= 2 ? greenCheck : grayCircle },
                    { label: 'Why Church Planting', icon: candidate.whyChurchPlantingCompleted ? greenCheck : grayCircle },
                    { label: 'References', icon: candidate.referencesCompleted ? greenCheck : grayCircle },
                    { label: 'Sending Church', icon: candidate.sendingChurchCompleted ? greenCheck : grayCircle },
                    {
                      label: 'Background Check',
                      icon: candidate.backgroundCheckStatus === 'received' ? greenCheck :
                        (candidate.backgroundCheckStatus === 'submitted' || candidate.backgroundCheckStatus === 'started') ? yellowClock : grayCircle,
                    },
                    {
                      label: `Prepare/Enrich${candidate.maritalStatus !== 'married' ? ' (N/A)' : ''}`,
                      icon: candidate.maritalStatus !== 'married' ? naIcon :
                        candidate.prepareEnrichStatus === 'complete' ? greenCheck :
                        (candidate.prepareEnrichStatus === 'scheduled' || candidate.prepareEnrichStatus === 'started') ? yellowClock : grayCircle,
                    },
                    {
                      label: `Spouse Interview${candidate.maritalStatus !== 'married' ? ' (N/A)' : ''}`,
                      icon: candidate.maritalStatus !== 'married' ? naIcon :
                        candidate.spouseInterviewSubmitted ? greenCheck :
                        (candidate.spouseInterviewStatus === 'scheduled') ? yellowClock : grayCircle,
                    },
                    { label: 'Theological Survey', icon: candidate.theologyCompleted ? greenCheck : grayCircle },
                    { label: 'Spiritual Health', icon: candidate.spiritualHealthCompleted ? greenCheck : grayCircle },
                    { label: 'Ministry Transitions', icon: candidate.ministryTransitionsCompleted ? greenCheck : grayCircle },
                  ];

                  return items.map((item, idx) => (
                    <div key={item.label} className={`flex items-center justify-between py-2 ${idx < items.length - 1 ? 'border-b border-gray-100' : ''}`}>
                      <span className="text-sm text-gray-700">{item.label}</span>
                      {item.icon}
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* References Section */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-900">References</h2>
                <span className="text-sm text-gray-500">
                  {references.filter(r => r.contactedByHub).length} of {references.length} contacted
                </span>
              </div>
              {references.length > 0 ? (
                <div className="space-y-3">
                  {references.map((reference) => (
                    <div key={reference.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-gray-900">{reference.name}</h3>
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                              {reference.relationshipType}
                            </span>
                            {reference.contactedByHub && (
                              <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 flex items-center gap-1">
                                <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Contacted
                              </span>
                            )}
                          </div>
                          <div className="mt-2 space-y-1">
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Email:</span> {reference.email}
                            </p>
                            {reference.phone && (
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">Phone:</span> {reference.phone}
                              </p>
                            )}
                            {reference.organization && (
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">Organization:</span> {reference.organization}
                                {reference.position && ` - ${reference.position}`}
                              </p>
                            )}
                            {reference.yearsKnown && (
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">Years Known:</span> {reference.yearsKnown}
                              </p>
                            )}
                            {reference.relationshipDescription && (
                              <p className="text-sm text-gray-600 mt-2">
                                {reference.relationshipDescription}
                              </p>
                            )}
                          </div>
                          {reference.contactedByHub && reference.contactedByHubNotes && (
                            <div className="mt-3 p-2 bg-gray-50 rounded">
                              <p className="text-xs font-medium text-gray-700">Contact Notes:</p>
                              <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{reference.contactedByHubNotes}</p>
                              {reference.contactedByHubAt && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Contacted on {new Date(reference.contactedByHubAt).toLocaleDateString()}
                                  {reference.contactedByUser && ` by ${reference.contactedByUser.firstName} ${reference.contactedByUser.lastName}`}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleMarkReferenceContacted(reference)}
                          className={`ml-4 px-3 py-1 text-xs font-medium rounded-md ${
                            reference.contactedByHub
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-primary-600 text-white hover:bg-primary-700'
                          }`}
                        >
                          {reference.contactedByHub ? 'Unmark' : 'Mark Contacted'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No references submitted yet</p>
              )}
            </div>

            {/* Prepare/Enrich Status */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-900">Prepare/Enrich</h2>
                {candidate.maritalStatus === 'married' && (
                  <button
                    onClick={() => {
                      setPrepareEnrichStatus(candidate.prepareEnrichStatus || 'not_started');
                      setPrepareEnrichNotes(candidate.prepareEnrichNotes || '');
                      setShowPrepareEnrichModal(true);
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Update
                  </button>
                )}
              </div>
              {candidate.maritalStatus === 'married' ? (
                <div className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="mt-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        candidate.prepareEnrichStatus === 'complete' ? 'bg-green-100 text-green-800' :
                        candidate.prepareEnrichStatus === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                        candidate.prepareEnrichStatus === 'started' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {candidate.prepareEnrichStatus === 'not_started' && 'Not Started'}
                        {candidate.prepareEnrichStatus === 'started' && 'Started'}
                        {candidate.prepareEnrichStatus === 'scheduled' && 'Scheduled'}
                        {candidate.prepareEnrichStatus === 'complete' && 'Complete'}
                      </span>
                    </dd>
                  </div>
                  {candidate.prepareEnrichRequestedAt && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Requested</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {new Date(candidate.prepareEnrichRequestedAt).toLocaleDateString()}
                      </dd>
                    </div>
                  )}
                  {candidate.prepareEnrichStatusUpdatedAt && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {new Date(candidate.prepareEnrichStatusUpdatedAt).toLocaleDateString()}
                      </dd>
                    </div>
                  )}
                  {candidate.prepareEnrichNotes && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Notes</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{candidate.prepareEnrichNotes}</dd>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Not applicable (candidate is not married)</p>
              )}
            </div>

            {/* Spiritual/Emotional Health Status */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Spiritual/Emotional Health Assessment</h2>
              <div className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Candidate Survey</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      candidate.spiritualHealthCandidateSubmitted ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {candidate.spiritualHealthCandidateSubmitted ? 'Submitted' : 'Not Submitted'}
                    </span>
                  </dd>
                </div>
                {candidate.maritalStatus === 'married' && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Spouse Survey</dt>
                    <dd className="mt-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        candidate.spiritualHealthSpouseSubmitted ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {candidate.spiritualHealthSpouseSubmitted ? 'Submitted' : 'Not Submitted'}
                      </span>
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Admin Review</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      candidate.spiritualHealthCompleted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {candidate.spiritualHealthCompleted ? 'Complete' : 'Pending Review'}
                    </span>
                  </dd>
                </div>
                {candidate.spiritualHealthNotes && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Notes</dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{candidate.spiritualHealthNotes}</dd>
                  </div>
                )}
              </div>
            </div>

            {/* Background Check Status */}
            {candidate.backgroundCheckSubmitted && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Background Check Status</h2>
                <div className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="mt-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        candidate.backgroundCheckStatus === 'received' ? 'bg-green-100 text-green-800' :
                        candidate.backgroundCheckStatus === 'submitted' ? 'bg-blue-100 text-blue-800' :
                        candidate.backgroundCheckStatus === 'started' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {candidate.backgroundCheckStatus === 'not_started' && 'Not Started'}
                        {candidate.backgroundCheckStatus === 'started' && 'Started'}
                        {candidate.backgroundCheckStatus === 'submitted' && 'Submitted'}
                        {candidate.backgroundCheckStatus === 'received' && 'Received'}
                      </span>
                    </dd>
                  </div>
                  {candidate.backgroundCheckSubmittedAt && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Submitted</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {new Date(candidate.backgroundCheckSubmittedAt).toLocaleDateString()}
                      </dd>
                    </div>
                  )}
                  {candidate.backgroundCheckStatusUpdatedAt && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {new Date(candidate.backgroundCheckStatusUpdatedAt).toLocaleDateString()}
                      </dd>
                    </div>
                  )}
                  {candidate.backgroundCheckNotes && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Notes</dt>
                      <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{candidate.backgroundCheckNotes}</dd>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Photos */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Photos</h2>
              <div className="space-y-4">
                {candidate.headshotImagePath ? (
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">Headshot</p>
                    <img 
                      src={getImageUrl(candidate.headshotImagePath, { width: 512, quality: 70, format: 'webp' })}
                      alt="Headshot"
                      className="w-full rounded-lg object-cover"
                    />
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No headshot uploaded</p>
                )}
                
                {candidate.familyPhotoImagePath ? (
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">Family Photo</p>
                    <img 
                      src={getImageUrl(candidate.familyPhotoImagePath, { width: 1200, quality: 70, format: 'webp' })}
                      alt="Family"
                      className="w-full rounded-lg object-cover"
                    />
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No family photo uploaded</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Status Update Modal */}
        {showStatusModal && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowStatusModal(false)}></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Update Candidate Status
                  </h3>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="">Select status...</option>
                    <option value="exploring">Exploring</option>
                    <option value="preassessment">Pre-Assessment</option>
                    <option value="postponed">Postponed</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={!newStatus || updateStatusMutation.isPending}
                    onClick={() => updateStatusMutation.mutate(newStatus)}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    Update
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowStatusModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Prepare/Enrich Update Modal */}
        {showPrepareEnrichModal && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowPrepareEnrichModal(false)}></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Update Prepare/Enrich Status
                  </h3>
                  
                  <div className="mb-4">
                    <label htmlFor="prepareEnrichStatus" className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      id="prepareEnrichStatus"
                      value={prepareEnrichStatus}
                      onChange={(e) => setPrepareEnrichStatus(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value="not_started">Not Started</option>
                      <option value="started">Started</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="complete">Complete</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="prepareEnrichNotes" className="block text-sm font-medium text-gray-700 mb-1">
                      Notes (Optional)
                    </label>
                    <textarea
                      id="prepareEnrichNotes"
                      rows={4}
                      value={prepareEnrichNotes}
                      onChange={(e) => setPrepareEnrichNotes(e.target.value)}
                      placeholder="Add any notes about this Prepare/Enrich session..."
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={updatePrepareEnrichMutation.isPending}
                    onClick={() => updatePrepareEnrichMutation.mutate({ 
                      status: prepareEnrichStatus, 
                      notes: prepareEnrichNotes || undefined 
                    })}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {updatePrepareEnrichMutation.isPending ? 'Updating...' : 'Update'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPrepareEnrichModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mark Reference Contacted Modal */}
        {showReferenceModal && selectedReference && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowReferenceModal(false)}></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Mark Reference Contacted
                  </h3>
                  
                  <div className="mb-4">
                    <p className="text-sm text-gray-700 mb-2">
                      <span className="font-medium">Reference:</span> {selectedReference.name}
                    </p>
                    <p className="text-sm text-gray-700 mb-2">
                      <span className="font-medium">Email:</span> {selectedReference.email}
                    </p>
                    {selectedReference.phone && (
                      <p className="text-sm text-gray-700 mb-2">
                        <span className="font-medium">Phone:</span> {selectedReference.phone}
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label htmlFor="referenceNotes" className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Notes (Optional)
                    </label>
                    <textarea
                      id="referenceNotes"
                      rows={4}
                      value={referenceNotes}
                      onChange={(e) => setReferenceNotes(e.target.value)}
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      placeholder="Add any notes about your contact with this reference..."
                    />
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={markReferenceContactedMutation.isPending}
                    onClick={submitReferenceContacted}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {markReferenceContactedMutation.isPending ? 'Saving...' : 'Mark Contacted'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowReferenceModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteModal(false)}></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
                        Delete Candidate
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 mb-2">
                          Are you sure you want to delete <span className="font-semibold">{candidate.name}</span>?
                        </p>
                        <p className="text-sm text-gray-500">
                          This action cannot be undone. All candidate data, including photos, responses, and progress will be permanently removed.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={deleteCandidateMutation.isPending}
                    onClick={() => deleteCandidateMutation.mutate()}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {deleteCandidateMutation.isPending ? 'Deleting...' : 'Delete Candidate'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={deleteCandidateMutation.isPending}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OnboardingDetail;
